// Bubble layout: color per conversation + vertical de-overlap so concurrent
// conversations never cover each other. Pure + tested.
import { CONVO_COLORS, TUNING } from '../constants.js';
import type { RuntimeNpc } from '../types.js';

export interface BubbleAnchor {
  convoId: string;
  x: number; // midpoint between the two speakers (world px)
  y: number; // above the higher (smaller y) speaker head
  color: string;
  stackLane: number; // 0..n — offset multiplier to avoid overlap
}

// Assign deterministic color by conversation start order.
export function colorForConvo(index: number): string {
  return CONVO_COLORS[index % CONVO_COLORS.length];
}

/** Evenly spaced hues so a small cast still reads as different people. */
export function assignCharacterBorderColors(ids: string[]): Record<string, string> {
  const n = Math.max(ids.length, 2);
  const out: Record<string, string> = {};
  ids.forEach((id, i) => {
    out[id] = `hsl(${Math.round((i * 360) / n) % 360} 95% 46%)`;
  });
  return out;
}

export function speakerBorderColor(
  speaker: string,
  npcs: Array<{ id: string; firstName: string; lastName: string }>,
  colors: Record<string, string>,
  fallback: string,
): string {
  const npc = npcs.find((n) => `${n.firstName} ${n.lastName}` === speaker);
  return (npc && colors[npc.id]) || fallback;
}

// Lanes: sort convos by anchor y, then greedily assign the lowest lane whose
// rendered position keeps minGap from every already-placed nearby anchor.
export function layoutBubbles(
  convos: Array<{ id: string; ax: number; ay: number; bx: number; by: number; color: string }>,
  minGapPx = 64,
): BubbleAnchor[] {
  const sorted = [...convos].sort((a, b) => Math.min(a.ay, a.by) - Math.min(b.ay, b.by));
  const out: BubbleAnchor[] = [];
  for (const c of sorted) {
    const baseY = Math.min(c.ay, c.by) - 52;
    const baseX = (c.ax + c.bx) / 2;
    let lane = 0;
    // Find first lane with no nearby anchor within minGap vertically.
    while (lane < 8) {
      const y = baseY - lane * minGapPx;
      const clash = out.some((o) => Math.abs(o.x - baseX) < 180 && Math.abs(o.y - y) < minGapPx);
      if (!clash) break;
      lane++;
    }
    out.push({ convoId: c.id, x: baseX, y: baseY - lane * minGapPx, color: c.color, stackLane: lane });
  }
  return out;
}

// Speaker anchor: the bubble floats over whoever is currently talking,
// switching heads as the turn passes. Unknown speaker → midpoint fallback.
export function speakerAnchor(opts: {
  speaker: string;
  aName: string;
  bName: string;
  ax: number;
  ay: number;
  bx: number;
  by: number;
}): { x: number; y: number } {
  if (opts.speaker === opts.aName) return { x: opts.ax, y: opts.ay };
  if (opts.speaker === opts.bName) return { x: opts.bx, y: opts.by };
  return { x: (opts.ax + opts.bx) / 2, y: Math.min(opts.ay, opts.by) };
}

// Edge awareness: nudge a speaker-centered bubble so it stays fully
// on-canvas. Returns the shifted center x; the tail keeps pointing at the
// speaker (clamped inside the bubble body by clampTailX).
export function clampBubbleX(ax: number, bubbleW: number, worldW: number, pad = 6): number {
  if (bubbleW + pad * 2 >= worldW) return worldW / 2; // wider than room: center it
  const half = bubbleW / 2 + pad;
  return Math.min(worldW - half, Math.max(half, ax));
}

export function clampTailX(tailX: number, centerX: number, bubbleW: number): number {
  const edge = bubbleW / 2 - 12;
  return Math.min(centerX + edge, Math.max(centerX - edge, tailX));
}

// Hover priority: hovering an NPC (or their bubble) puts that NPC's whole
// conversation on top. Bubble hover wins (bubbles render above bodies).
// Moving to another conversation's NPC/bubble swaps priority immediately;
// hovering nobody (or a non-talking NPC) yields null = default order.
export function hoverPriority(
  hoverNpcId: string | null,
  hoverBubbleId: string | null,
  convos: Array<{ id: string; aId: string; bId: string }>,
): string | null {
  if (hoverBubbleId && convos.some((c) => c.id === hoverBubbleId)) return hoverBubbleId;
  if (hoverNpcId) return convos.find((c) => c.aId === hoverNpcId || c.bId === hoverNpcId)?.id ?? null;
  return null;
}

// Waiting pairs: claimed each other (busyWith set) but their script hasn't
// generated yet — no active convo. They get a "…" thinking pill so the
// minutes-long LLM wait reads as thinking, not frozen.
// talkingIds = npc ids already covered by an active convo (aId + bId).
export function waitingPairs(
  runtime: RuntimeNpc[],
  talkingIds: Set<string>,
): Array<{ x: number; y: number }> {
  const seen = new Set<string>();
  const out: Array<{ x: number; y: number }> = [];
  for (const r of runtime) {
    if (!r.busyWith || seen.has(r.npcId)) continue;
    const o = runtime.find((x) => x.npcId === r.busyWith);
    if (!o) continue;
    seen.add(r.npcId);
    seen.add(o.npcId);
    if (!talkingIds.has(r.npcId) && !talkingIds.has(o.npcId)) {
      out.push({ x: (r.x + o.x) / 2, y: Math.min(r.y, o.y) });
    }
  }
  return out;
}

// Per-line timing: typewriter handled by renderer; this gives linger ms.
// mult scales the default (Setup setting, 0.75 = -25%).
export function lingerMsForLine(text: string, mult: number = TUNING.lingerMultiplier): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.round((TUNING.lingerBaseS + TUNING.lingerPerWordS * words) * mult * 1000);
}
